import React, { useEffect, useState } from 'react';
import {BrowserRouter as Router, Route, Switch, Redirect, useLocation} from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';

import Home from '../containers/Home.js';
import Login from '../containers/Login.js';
import Users from '../containers/Users';
import Organizations from '../containers/Organizations';
import AdminView from '../containers/AdminView';
import Appraisals from '../containers/Appraisals';
import NotFound from './NotFound.js';
import { About } from '../About';
import AdminRoute from './AdminRoute';
import UserProfile from '../UserProfile';
import { EditableProfile } from '../EditableProfile';
import UnconfirmedUser from './UnconfirmedUser';
import { LoginNavigationBar } from '../components/LoginNavigationBar';
import { DefaultNavigationBar } from '../components/DefaultNavigationBar';
import { Layout } from '../components/Layout';
import UpdatePasswordForm from '../containers/UpdatePasswordForm';
import ChangePasswordForm from '../containers/ChangePasswordForm';
import { SecondaryNavigationBar } from '../components/SecondaryNavigationBar.js';

/** ---------- Inline, v6-safe route guards ---------- **/

function AuthCheck({ children }) {
  const [state, setState] = useState('checking'); // 'checking' | 'authed' | 'guest'

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await getCurrentUser();               // throws if not signed in
        if (mounted) setState('authed');
      } catch {
        if (mounted) setState('guest');
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (state === 'checking') return null;      // or a spinner
  if (state === 'guest')   return <Redirect to="/login" />;
  return children;
}

function GuestOnly({ children }) {
  const [state, setState] = useState('checking'); // 'checking' | 'authed' | 'guest'

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await getCurrentUser();
        if (mounted) setState('authed');
      } catch {
        if (mounted) setState('guest');
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (state === 'checking') return null;
  if (state === 'authed')   return <Redirect to="/" />;
  return children;
}

// Drop-in wrappers that match your existing usage (component + props)
function AuthenticatedRouteInline({ component: Cmp, props: childProps, ...rest }) {
  return (
    <Route
      {...rest}
      render={(routeProps) => (
        <AuthCheck>
          <Cmp {...routeProps} {...childProps} />
        </AuthCheck>
      )}
    />
  );
}

function UnauthenticatedRouteInline({ component: Cmp, props: childProps, ...rest }) {
  return (
    <Route
      {...rest}
      render={(routeProps) => (
        <GuestOnly>
          <Cmp {...routeProps} {...childProps} />
        </GuestOnly>
      )}
    />
  );
}
/**---------------------------------------------------- */

function NavigationBar({ childProps }) {
  const location = useLocation();
  const currentPath = location.pathname;

  if (currentPath.includes("/login") ||
      currentPath === '/change' ||
      currentPath === '/change-password') {
    return <LoginNavigationBar />;
  } else if ((window.location.toString().endsWith("redirect=/")) ||
             currentPath === '/' ||
             currentPath === '/organizations' ||
             currentPath === '/users' ||
             currentPath === '/appraisals') {
    if (childProps.session) {
      return <DefaultNavigationBar isAdmin={childProps.isAdmin} onLogoutClicked={childProps.onLogoutClicked} />;
    } else {
      return <LoginNavigationBar />;
    }
  } else {
    return <SecondaryNavigationBar onLogoutClicked={childProps.onLogoutClicked} />;
  }
}

const Routes = ({ childProps }) => {
  return (
    <Router>
      <NavigationBar childProps={childProps} />
      <Layout>
        <Switch>
          <AuthenticatedRouteInline path="/" exact component={Home} props={childProps} />
          <Route path="/about" exact component={About} />
          <UnauthenticatedRouteInline path="/login" exact component={Login} props={childProps} />

          {/* Profile & account */}
          <AuthenticatedRouteInline path="/profile" component={UserProfile} props={childProps} />
          <AuthenticatedRouteInline path="/update" component={EditableProfile} props={childProps} />
          <AuthenticatedRouteInline path="/change" component={UpdatePasswordForm} props={childProps} />
          <AuthenticatedRouteInline path="/change-password" component={ChangePasswordForm} props={childProps} />

          {/* Admin-only areas */}
          <AdminRoute path='/organizations' exact component={Organizations} props={childProps} />
          <AdminRoute path='/users' exact component={Users} props={childProps} />
          <AdminRoute path='/adminview' exact component={AdminView} props={childProps} />

          {/* Was AdminRoute; now any authenticated user can access (org-or-admin on server) */}
          <AuthenticatedRouteInline path='/appraisals' exact component={Appraisals} props={childProps} />

          <Route path='/unconfirmed' exact component={UnconfirmedUser} />
          <Route component={NotFound} />
        </Switch>
      </Layout>
    </Router>
  );
};

export { Routes };
